import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Instagram, Facebook, Bike } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    toast({
      title: "Wiadomość wysłana!",
      description: "Skontaktujemy się z Tobą w ciągu 24 godzin.",
    });
    setFormData({ name: '', email: '', message: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <footer className="py-24 px-6 bg-gradient-to-t from-muted/20 to-background">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div>
            <h2 className="font-montserrat font-bold text-4xl mb-6">
              Skontaktuj się <span className="text-gradient-primary">z nami</span>
            </h2>
            <p className="text-xl text-muted-foreground font-inter mb-8">
              Masz pytania o tuning? Chcesz przedyskutować swój projekt? Napisz do nas!
            </p>

            <Card className="p-6 bg-card/50 backdrop-blur-sm border-accent/20">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-foreground font-inter font-medium">
                    Imię
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-2 bg-background/50 border-accent/30 focus:border-primary"
                    placeholder="Twoje imię"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-foreground font-inter font-medium">
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-2 bg-background/50 border-accent/30 focus:border-primary"
                    placeholder="twoj@email.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-foreground font-inter font-medium">
                    Wiadomość
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="mt-2 bg-background/50 border-accent/30 focus:border-primary min-h-[120px]"
                    placeholder="Opisz swój projekt, zadaj pytanie lub po prostu się przywitaj..."
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  variant="hero" 
                  size="lg" 
                  className="w-full interactive-scale neon-glow-cyan"
                >
                  <Mail className="w-5 h-5" />
                  Wyślij wiadomość
                </Button>
              </form>
            </Card>
          </div>

          {/* Company Info & Social */}
          <div className="lg:pl-12">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center neon-glow-cyan">
                  <Bike className="w-6 h-6 text-primary-foreground bike-wheel" />
                </div>
                <h3 className="font-montserrat font-bold text-3xl text-gradient-primary">
                  Pimp my Bike
                </h3>
              </div>
              
              <p className="text-muted-foreground font-inter leading-relaxed mb-6">
                Specjaliści od tuningu rowerów. Przekształcamy zwykłe rowery w unikatowe dzieła sztuki, 
                które odzwierciedlają Twoją osobowość i pasję do kolarstwa.
              </p>

              <div className="space-y-3 text-foreground font-inter">
                <p><strong>Misja:</strong> Tchnąć w każdy rower drugie życie</p>
                <p><strong>Wartości:</strong> Jakość • Kreatywność • Pasja</p>
              </div>
            </div>

            {/* Social Media */}
            <div className="mb-12">
              <h4 className="font-montserrat font-bold text-xl mb-4 text-foreground">
                Śledź nas w social media
              </h4>
              <div className="flex gap-4">
                <Button variant="outline" size="icon" className="border-primary/30 hover:border-primary hover:bg-primary/10">
                  <Instagram className="w-5 h-5 text-primary" />
                </Button>
                <Button variant="outline" size="icon" className="border-primary/30 hover:border-primary hover:bg-primary/10">
                  <Facebook className="w-5 h-5 text-primary" />
                </Button>
              </div>
            </div>

            {/* Motto - Full Width */}
            <div className="w-full bg-amber-500 py-8 px-4 -mx-4 lg:-mx-12">
              <div className="max-w-3xl mx-auto text-center">
                <blockquote>
                  <p className="font-montserrat font-extrabold text-2xl text-white mb-3">
                    "Tuninguj pasję"
                  </p>
                  <p className="text-white/90 font-inter font-medium">
                    Każdy rower ma swoją historię. Pozwól nam napisać następny rozdział.
                  </p>
                </blockquote>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="mt-16 pt-8 text-center bg-amber-50/50 -mx-4 lg:-mx-12 px-4 lg:px-12 py-6 rounded-t-2xl">
          <p className="text-amber-900/80 font-inter text-sm">
            © 2024 Pimp my Bike. Wszystkie prawa zastrzeżone. • Jedź tak, jak nikt inny • Twój rower, Twoje zasady
          </p>
        </div>
      </div>
    </footer>
  );
};